import {useRef, useState} from "react";
import * as openpgp from "openpgp";
import {ChatHistoryMessage} from "@inpriton/types/conferenceTypes";

export type ChatMessage = {
  id: string
  from: string
  to: string
  encrypted: string
  decrypted: string | null
  read: boolean
  fromName?: string
  // Set on served history only; a live message to a lost key stays a lock (RISKS.md/P2.18).
  unreadable?: boolean
}

// Every id of the key, not `getKeyID()`: messages are encrypted to its subkey.
const ownKeyIds: { [armoredKey: string]: Promise<string[]> } = {}
const keyIdsOf = (armoredKey: string) =>
  ownKeyIds[armoredKey] ??= openpgp.readKey({ armoredKey }).then(key => key.getKeyIDs().map(id => id.toHex()))

const isEncryptedTo = async (armoredMessage: string, keyIds: string[]) => {
  try {
    const message = await openpgp.readMessage({ armoredMessage })
    return message.getEncryptionKeyIDs().some(id => keyIds.includes(id.toHex()))
  } catch {
    return false
  }
}

// RISKS.md/P1.32: OpenPGP.js decrypts synchronously behind its awaits, so promises alone would run a
// served history as one long task. Not setTimeout: a hidden tab fires it at most once a second.
const nextTask = () => new Promise<void>(resolve => {
  const channel = new MessageChannel()
  channel.port1.onmessage = () => resolve()
  channel.port2.postMessage(null)
})

const useChat = (decrypt: (encrypted: string) => Promise<string>) => {
  const [ messages, setMessages ] = useState<ChatMessage[]>([])
  const [ typingUsers, setTypingUsers ] = useState<string[]>([])

  const messagesRef = useRef(messages)
  messagesRef.current = messages

  const typingUsersRef = useRef(typingUsers)
  typingUsersRef.current = typingUsers

  // RISKS.md/P2.78: bumped by clear(), so history still being read for an ended session is dropped.
  const generationRef = useRef(0)

  // Writers build on the ref, never on a render's `messages`, which lacks whatever was added since.
  const commit = (next: ChatMessage[]) => {
    messagesRef.current = next
    setMessages(next)
  }

  const decryptChainRef = useRef<Promise<void>>(Promise.resolve())

  // RISKS.md/P1.32: a link must never reject, or the chain skips every later message.
  const decryptInto = (id: string, encrypted: string) => {
    const generation = generationRef.current
    decryptChainRef.current = decryptChainRef.current.then(async () => {
      await nextTask()
      if (generation !== generationRef.current) {
        return
      }
      let decrypted = ''
      try {
        decrypted = await decrypt(encrypted)
      } catch (e) {
        console.error('decrypting a chat message failed', e)
      }
      commit(messagesRef.current.map(m => {
        if (m.id === id) {
          m.decrypted = decrypted
        }
        return m
      }))
    })
  }

  const onTyping = (data: any) => {
    if (data.value && !typingUsersRef.current.includes(data.from)) {
      setTypingUsers([
        ...typingUsersRef.current,
        data.from,
      ])
    }
    if (!data.value && typingUsersRef.current.includes(data.from)) {
      setTypingUsers(typingUsersRef.current.filter(uuid => uuid !== data.from))
    }
  }

  // RISKS.md/P2.78: skips a held id — a message stored after the join's subscribe also comes in the history.
  const onChatMessage = (data: any) => {
    if (typeof data.message === 'undefined') {
      console.error('received unkown message', data)
    } else if (!messagesRef.current.some(m => m.id === data.id)) {
      commit([
        ...messagesRef.current,
        {
          id: data.id,
          from: data.from,
          to: data.to,
          encrypted: data.message,
          decrypted: null,
          read: false,
        },
      ])

      decryptInto(data.id, data.message)
    }
  }

  const markRead = (id: string) => {
    const message = messagesRef.current.find(m => m.id === id)
    if (message && !message.read) {
      message.read = true
      commit([...messagesRef.current])
    }
  }

  const clear = () => {
    generationRef.current++
    commit([])
  }

  // RISKS.md/P2.78: merged by the server-minted id, since a relay can land before the history holding
  // it. Only a reconnect's new copies are unread: the tab missed them while its socket was down.
  const onHistory = (rows: ChatHistoryMessage[], { reconnect, publicKey }: { reconnect: boolean, publicKey: string }) => {
    if (!rows.length) {
      return
    }
    const generation = generationRef.current

    void (async () => {
      let readable: boolean[]
      try {
        const keyIds = await keyIdsOf(publicKey)
        readable = await Promise.all(rows.map(row => isEncryptedTo(row.message, keyIds)))
      } catch (e) {
        // Fail open: every copy is tried, and one that fails stays a lock (RISKS.md/P2.80).
        console.error('reading the chat history\'s recipient keys failed', e)
        readable = rows.map(() => true)
      }
      if (generation !== generationRef.current) {
        return
      }

      const held = new Map(messagesRef.current.map(m => [m.id, m]))
      const served = new Set(rows.map(row => row.id))
      const toDecrypt: ChatMessage[] = []
      const history = rows.map((row, i) => {
        const existing = held.get(row.id)
        if (existing) {
          existing.fromName = row.fromName
          return existing
        }
        const message: ChatMessage = {
          id: row.id,
          from: row.from,
          to: row.to,
          encrypted: row.message,
          decrypted: null,
          read: !reconnect,
          fromName: row.fromName,
          unreadable: !readable[i],
        }
        if (readable[i]) {
          toDecrypt.push(message)
        }
        return message
      })

      commit([...history, ...messagesRef.current.filter(m => !served.has(m.id))])
      toDecrypt.forEach(m => decryptInto(m.id, m.encrypted))
    })()
  }

  return {
    messages, setMessages,
    typingUsers, setTypingUsers,
    onTyping, onChatMessage,
    onHistory, markRead, clear,
  }
}

export default useChat
